import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api/client";
import { setSocketToken } from "../api/rooms";
import { AppHeader } from "../components/AppHeader";
import { pixelTheme } from "../theme/pixel";
import { defaultBtn, primaryBtn, withBtnStyle } from "../theme/styles";
import { inertAttrs } from "../logic/inert";

const MIN_PASSWORD = 10;

/**
 * Authenticated settings page for changing the user's own password.
 * Linked from the header menu (CHANGE PASSWORD). A successful change
 * rotates the user's session_salt server-side — the response carries
 * a fresh socket_token + csrf_token so this tab stays usable; any
 * other open tabs lose their socket on the next reconnect.
 */
export function ChangePasswordPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();

  const [user, setUser] = useState<api.User | null>(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.getMe();
        if (cancelled) return;
        if (me.user.is_guest) {
          // Guests have no password to change; bounce them.
          navigate("/lobby", { replace: true });
          return;
        }
        setUser(me.user);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof api.ApiError && err.status === 401) {
          navigate("/login?returnTo=/account/change-password", { replace: true });
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!current || next.length < MIN_PASSWORD || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await api.changePassword(current, next);
      // Server rotated session_salt and minted a new socket token —
      // refresh ours so the WS stays valid.
      setSocketToken(resp.socket_token);
      setDone(true);
      setCurrent("");
      setNext("");
    } catch (err) {
      const msg =
        err instanceof api.ApiError && err.status === 401
          ? "Current password is incorrect."
          : err instanceof Error
            ? err.message
            : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const labelStyle: React.CSSProperties = {
    display: "grid",
    gap: 6,
    fontFamily: theme.fontDisplay,
    fontSize: 11,
    letterSpacing: theme.letterSpacing,
    color: theme.inkDim,
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: theme.fontBody,
    fontSize: 20,
    padding: "8px 10px",
    background: theme.bgDeep,
    color: theme.ink,
    border: `2px solid ${theme.panelEdge}`,
  };

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "24px 20px",
      }}
    >
      <div
        style={{
          maxWidth: 880,
          margin: "0 auto",
          display: "grid",
          gap: 20,
        }}
      >
        <AppHeader user={user} />

        <form
          onSubmit={onSubmit}
          style={{
            background: theme.panel,
            border: `2px solid ${theme.panelEdgeStrong}`,
            padding: 28,
            display: "grid",
            gap: 18,
            maxWidth: 460,
            width: "100%",
            justifySelf: "center",
          }}
        >
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 16,
              letterSpacing: theme.letterSpacing,
              color: theme.accent,
              textAlign: "center",
            }}
          >
            CHANGE PASSWORD
          </div>

          {done ? (
            <>
              <div style={{ fontSize: 14, color: theme.inkDim, lineHeight: 1.5 }}>
                Done. Your new password is in effect. Any other tabs
                you were logged in on have been signed out.
              </div>
              <button
                type="button"
                onClick={() => navigate("/lobby")}
                {...withBtnStyle(defaultBtn(theme, false), {
                  fontSize: 12,
                  padding: "10px 16px",
                })}
              >
                BACK TO LOBBY
              </button>
            </>
          ) : (
            <>
              <label style={labelStyle}>
                CURRENT PASSWORD
                <input
                  autoFocus
                  type="password"
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => {
                    setCurrent(e.target.value);
                    setError(null);
                  }}
                  disabled={submitting}
                  style={inputStyle}
                  className="doi-input"
                />
              </label>
              <label style={labelStyle}>
                NEW PASSWORD
                <input
                  type="password"
                  autoComplete="new-password"
                  value={next}
                  onChange={(e) => {
                    setNext(e.target.value);
                    setError(null);
                  }}
                  disabled={submitting}
                  placeholder={`at least ${MIN_PASSWORD} characters`}
                  style={inputStyle}
                  className="doi-input"
                />
                <span style={{ fontSize: 11, color: theme.inkFaint }}>
                  {next.length === 0
                    ? `Minimum ${MIN_PASSWORD} characters.`
                    : next.length < MIN_PASSWORD
                      ? `${MIN_PASSWORD - next.length} more character${
                          MIN_PASSWORD - next.length === 1 ? "" : "s"
                        } to go.`
                      : "Long enough."}
                </span>
              </label>
              {error && (
                <div style={{ color: theme.accent, fontSize: 14 }}>{error}</div>
              )}
              <button
                type="submit"
                {...inertAttrs(
                  submitting || !current || next.length < MIN_PASSWORD,
                )}
                {...withBtnStyle(primaryBtn(theme, submitting), {
                  fontSize: 12,
                  padding: "10px 16px",
                  cursor: submitting ? "wait" : undefined,
                })}
              >
                {submitting ? "…" : "CHANGE PASSWORD"}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
